import React, { useMemo, useState } from 'react';
import { type Listing, updateListing, setAllTicketTiersSoldOut } from '../../services/listings';
import { deleteListing } from '../../services/listings';
import { auth } from '../../lib/firebase';
import { useNavigate } from 'react-router-dom';

interface ManageListingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  listings: Listing[];
  onChange?: () => void; // notify parent to refresh
}

const ManageListingsModal: React.FC<ManageListingsModalProps> = ({ isOpen, onClose, listings, onChange }) => {
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, Partial<Listing>>>({});
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const navigate = useNavigate();
  const currentUid = auth.currentUser?.uid || null;

  const sorted = useMemo(() => {
    return [...(listings || [])].sort((a, b) => {
      const at = a.createdAt?.toMillis?.() || 0;
      const bt = b.createdAt?.toMillis?.() || 0;
      return bt - at;
    });
  }, [listings]);

  const isOwner = (item: Listing) => {
    if (!item?.sellerId || !currentUid) return false;
    return item.sellerId === currentUid;
  };

  if (!isOpen) return null;

  const startEdit = (id: string, item: Listing) => {
    setEditing(prev => ({
      ...prev,
      [id]: {
        title: item.title,
        price: item.price,
        location: item.location,
        description: item.description,
        specifications: item.specifications || {}
      }
    }));
  };

  const cancelEdit = (id: string) => {
    setEditing(prev => { const next = { ...prev }; delete next[id]; return next; });
  };

  const saveEdit = async (id: string) => {
    const patch = editing[id];
    if (!patch) return;
    try {
      setUpdatingId(id);
      await updateListing(id, patch);
      onChange?.();
    } catch {}
    finally {
      setUpdatingId(null);
      cancelEdit(id);
    }
  };

  const toggleListingSoldOut = async (item: Listing) => {
    if (!item.id) return;
    try {
      setUpdatingId(item.id);
      await updateListing(item.id, { soldOut: !item.soldOut });
      onChange?.();
    } catch {}
    finally { setUpdatingId(null); }
  };

  const toggleTierSold = async (item: Listing, idx: number, next: boolean) => {
    if (!item.id) return;
    const tiers = (item.ticketTiers || []).map((t, i) => i === idx ? { ...t, soldOut: next } : t);
    try {
      setUpdatingId(item.id);
      await updateListing(item.id, { ticketTiers: tiers });
      const allSold = tiers.length > 0 && tiers.every(t => t.soldOut);
      await updateListing(item.id, { soldOut: allSold });
      onChange?.();
    } catch {}
    finally { setUpdatingId(null); }
  };

  const markAllTiers = async (item: Listing, soldOut: boolean) => {
    if (!item.id) return;
    try {
      setUpdatingId(item.id);
      await setAllTicketTiersSoldOut(item.id, soldOut);
      await updateListing(item.id, { soldOut });
      onChange?.();
    } catch {}
    finally { setUpdatingId(null); }
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      setUpdatingId(confirmDeleteId);
      await deleteListing(confirmDeleteId);
      onChange?.();
    } catch {}
    finally {
      setUpdatingId(null);
      setConfirmDeleteId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
      <div className="w-full sm:max-w-3xl bg-white rounded-t-xl sm:rounded-xl shadow-xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Manage Listings</h2>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="p-4 max-h-[70vh] sm:max-h-[75vh] overflow-y-auto">
          {sorted.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-store-line text-5xl text-gray-300 mb-2"></i>
              <p className="text-gray-600">No listings to manage</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sorted.map(item => (
                <div key={item.id} className="border rounded-lg p-3">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm px-2 py-0.5 rounded-full border text-gray-600">{item.type || 'item'}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${item.soldOut ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{item.soldOut ? 'sold out' : 'active'}</span>
                      </div>
                      {!editing[item.id || ''] ? (
                        <>
                          <h3 className="text-base font-semibold text-gray-900">{item.title}</h3>
                          <div className="text-sm text-gray-600">₦{(item.price ?? 0).toLocaleString()}</div>
                          {item.location && <div className="text-xs text-gray-500">{item.location}</div>}
                        </>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input
                            className="px-2 py-1 border rounded"
                            placeholder="Title"
                            value={editing[item.id || '']?.title as string || ''}
                            onChange={(e) => setEditing(prev => ({ ...prev, [item.id || '']: { ...prev[item.id || ''], title: e.target.value } }))}
                          />
                          <input
                            className="px-2 py-1 border rounded"
                            type="number"
                            placeholder="Price"
                            value={editing[item.id || '']?.price as number || 0}
                            onChange={(e) => setEditing(prev => ({ ...prev, [item.id || '']: { ...prev[item.id || ''], price: Number(e.target.value) } }))}
                          />
                          <input
                            className="px-2 py-1 border rounded"
                            placeholder="Location"
                            value={editing[item.id || '']?.location as string || ''}
                            onChange={(e) => setEditing(prev => ({ ...prev, [item.id || '']: { ...prev[item.id || ''], location: e.target.value } }))}
                          />
                          <textarea
                            className="px-2 py-1 border rounded"
                            placeholder="Description"
                            value={editing[item.id || '']?.description as string || ''}
                            onChange={(e) => setEditing(prev => ({ ...prev, [item.id || '']: { ...prev[item.id || ''], description: e.target.value } }))}
                          />

                          {item.type === 'house' && (
                            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-2 border-t pt-2 mt-2">
                              <input
                                className="px-2 py-1 border rounded"
                                placeholder="Bedrooms"
                                type="number"
                                value={(editing[item.id || '']?.specifications as any)?.bedrooms || (item.specifications?.bedrooms || '')}
                                onChange={(e) => setEditing(prev => ({
                                  ...prev,
                                  [item.id || '']: {
                                    ...prev[item.id || ''],
                                    specifications: {
                                      ...(prev[item.id || '']?.specifications || item.specifications || {}),
                                      bedrooms: e.target.value
                                    }
                                  }
                                }))}
                              />
                              <input
                                className="px-2 py-1 border rounded"
                                placeholder="Bathrooms"
                                type="number"
                                value={(editing[item.id || '']?.specifications as any)?.bathrooms || (item.specifications?.bathrooms || '')}
                                onChange={(e) => setEditing(prev => ({
                                  ...prev,
                                  [item.id || '']: {
                                    ...prev[item.id || ''],
                                    specifications: {
                                      ...(prev[item.id || '']?.specifications || item.specifications || {}),
                                      bathrooms: e.target.value
                                    }
                                  }
                                }))}
                              />
                              <input
                                className="px-2 py-1 border rounded"
                                placeholder="Lease term (e.g., 12 months)"
                                value={(editing[item.id || '']?.specifications as any)?.leaseTerm || (item.specifications?.leaseTerm || '')}
                                onChange={(e) => setEditing(prev => ({
                                  ...prev,
                                  [item.id || '']: {
                                    ...prev[item.id || ''],
                                    specifications: {
                                      ...(prev[item.id || '']?.specifications || item.specifications || {}),
                                      leaseTerm: e.target.value
                                    }
                                  }
                                }))}
                              />
                              <input
                                className="px-2 py-1 border rounded"
                                placeholder="Utilities included"
                                value={(editing[item.id || '']?.specifications as any)?.utilitiesIncluded || (item.specifications?.utilitiesIncluded || '')}
                                onChange={(e) => setEditing(prev => ({
                                  ...prev,
                                  [item.id || '']: {
                                    ...prev[item.id || ''],
                                    specifications: {
                                      ...(prev[item.id || '']?.specifications || item.specifications || {}),
                                      utilitiesIncluded: e.target.value
                                    }
                                  }
                                }))}
                              />
                              <input
                                className="px-2 py-1 border rounded sm:col-span-2"
                                placeholder="Address"
                                value={(editing[item.id || '']?.specifications as any)?.address || (item.specifications?.address || '')}
                                onChange={(e) => setEditing(prev => ({
                                  ...prev,
                                  [item.id || '']: {
                                    ...prev[item.id || ''],
                                    specifications: {
                                      ...(prev[item.id || '']?.specifications || item.specifications || {}),
                                      address: e.target.value
                                    }
                                  }
                                }))}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {!editing[item.id || ''] ? (
                        <>
                          <button
                            className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                            onClick={() => startEdit(item.id!, item)}
                            disabled={updatingId === item.id || !isOwner(item)}
                            title={!isOwner(item) ? 'Only the owner can edit' : ''}
                          >
                            Edit
                          </button>
                          <button
                            className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                            onClick={() => setConfirmDeleteId(item.id!)}
                            disabled={updatingId === item.id || !isOwner(item)}
                            title={!isOwner(item) ? 'Only the owner can delete' : ''}
                          >
                            Delete
                          </button>
                          <button
                            className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                            onClick={() => toggleListingSoldOut(item)}
                            disabled={updatingId === item.id || !isOwner(item)}
                            title={!isOwner(item) ? 'Only the owner can change status' : ''}
                          >
                            {item.soldOut ? 'Mark Available' : 'Mark Sold Out'}
                          </button>
                          <button
                            className="px-3 py-1 text-sm bg-purple-600 text-white rounded hover:bg-purple-700"
                            onClick={() => navigate(`/seller-dashboard?tab=analytics&listingId=${item.id}`)}
                            title="View analytics"
                          >
                            View Analytics
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700" onClick={() => saveEdit(item.id!)} disabled={updatingId === item.id}>Save</button>
                          <button className="px-3 py-1 text-sm border rounded hover:bg-gray-50" onClick={() => cancelEdit(item.id!)} disabled={updatingId === item.id}>Cancel</button>
                        </>
                      )}
                    </div>
                  </div>

                  {item.type === 'ticket' && Array.isArray(item.ticketTiers) && item.ticketTiers.length > 0 && (
                    <div className="mt-3 border-t pt-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Ticket Tiers</span>
                        <div className="flex gap-2">
                          <button className="px-2 py-1 text-xs border rounded hover:bg-gray-50" onClick={() => markAllTiers(item, true)} disabled={updatingId === item.id}>Mark all sold out</button>
                          <button className="px-2 py-1 text-xs border rounded hover:bg-gray-50" onClick={() => markAllTiers(item, false)} disabled={updatingId === item.id}>Mark all available</button>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {item.ticketTiers.map((t, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <span className="text-sm text-gray-700">{t.name} - ₦{t.price?.toLocaleString()}</span>
                            <button className={`px-2 py-1 text-xs rounded ${t.soldOut ? 'bg-red-600 text-white' : 'bg-green-600 text-white'}`} onClick={() => toggleTierSold(item, idx, !t.soldOut)} disabled={updatingId === item.id}>
                              {t.soldOut ? 'Mark available' : 'Mark sold out'}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {item.type && (
                    <p className="mt-2 text-xs text-gray-500">Type is locked to "{item.type}" and cannot be changed.</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delete confirmation */}
        {confirmDeleteId && (
          <div className="border-t p-4 flex items-center justify-between bg-red-50">
            <span className="text-sm text-red-700">Confirm delete this listing? This action cannot be undone.</span>
            <div className="flex gap-2">
              <button className="px-3 py-1 text-sm border rounded hover:bg-white" onClick={() => setConfirmDeleteId(null)} disabled={updatingId === confirmDeleteId}>Cancel</button>
              <button className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700" onClick={confirmDelete} disabled={updatingId === confirmDeleteId}>Delete</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageListingsModal;